import React, { useState, useEffect, useRef } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Assignment as TaskIcon,
  Message as MessageIcon,
  Folder as ProjectIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationService, taskService, projectService } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const audioContextRef = useRef(null);
  const lastCountRef = useRef(0);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, []);

  const playNotificationSound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Nice "ding" sound
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
      osc.frequency.exponentialRampToValueAtTime(1046.5, ctx.currentTime + 0.1); // C6
      
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) {
      console.error('Audio play failed', e);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await notificationService.getUnread();
      const newNotifications = response.data.data;
      
      // Play sound if new notifications arrived
      if (newNotifications.length > lastCountRef.current) {
        playNotificationSound();
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newNotifications.length);
      lastCountRef.current = newNotifications.length;
    } catch (error) {
      // Silently fail on network errors to avoid console spam
      if (error.code !== 'ERR_NETWORK' && error.code !== 'ECONNABORTED') {
        console.error('Error fetching notifications:', error);
      }
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notification) => {
    try {
      await notificationService.markAsRead(notification.id);
      fetchNotifications(); // Refresh list
      handleClose();
      
      const { type, entity_type, entity_id, related_type, related_id } = notification;

      // Handle Project Invitation — redirect to acceptance page
      if (type === 'project_invitation') {
        try {
          const res = await projectService.getMyInviteToken(entity_id);
          if (res.data?.token) {
            navigate(`/invite/${res.data.token}`);
          } else {
            // Invitation déjà acceptée ou expirée : aller au projet
            navigate(`/projects/${entity_id}`);
          }
        } catch {
          navigate(`/projects/${entity_id}`);
        }
        return;
      }

      // Handle Access Request
      if (type === 'access_request') {
         navigate(`/projects/${entity_id}?action=requests`);
         return;
      }

      // Handle Messages/Info
      if (type === 'info' || type === 'new_message') {
         if (entity_type === 'project') {
             // Check if it's a chat message or general update
             if (notification.title.toLowerCase().includes('message')) {
                 navigate(`/projects/${entity_id}?tab=discussion`);
             } else {
                 navigate(`/projects/${entity_id}`);
             }
             return;
         } else if (entity_type === 'task') {
             try {
                const res = await taskService.getById(entity_id);
                if (res.data?.task?.project_id) {
                    navigate(`/projects/${res.data.task.project_id}`);
                }
             } catch(e) {
                 // console.error("Nav error", e);
             }
             return;
         }
      }

      // Fallback/Legacy
      if (related_type === 'project') {
        navigate(`/projects/${related_id}`);
      } else if (entity_type === 'project') {
        navigate(`/projects/${entity_id}`);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      fetchNotifications();
      handleClose();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'task_assigned':
      case 'task_updated':
        return <TaskIcon color="primary" />;
      case 'new_message':
      case 'info':
        return <MessageIcon color="secondary" />;
      case 'project_update':
      case 'access_request':
        return <ProjectIcon color="info" />;
      default:
        return <InfoIcon color="action" />;
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 360,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllAsRead}>
              Tout marquer comme lu
            </Button>
          )}
        </Box>
        <Divider />
        
        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Aucune nouvelle notification
            </Typography>
          </MenuItem>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification) => (
              <React.Fragment key={notification.id}>
                <ListItem 
                  alignItems="flex-start" 
                  button 
                  onClick={() => handleMarkAsRead(notification)}
                  sx={{ bgcolor: notification.is_read ? 'transparent' : 'action.hover' }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'background.paper' }}>
                      {getIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={notification.title}
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          sx={{ display: 'block' }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: fr })}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;